{
  "title": "Users & roles",
  "subtitle": "Who can use the app, what they can do, and in which locations.",
  "newUser": "New user",
  "tabs": {
    "people": "People · {{count}}",
    "roles": "Roles · {{count}}"
  },
  "list": {
    "title": "People · {{active}} active, {{deactivated}} deactivated",
    "neverDeleted": "Users are never deleted, so their history stays readable",
    "person": "Person",
    "workplaces": "Can work in",
    "lastSignIn": "Last sign-in",
    "status": "Status",
    "resetPassword": "Reset password",
    "allLocations": "All locations",
    "allLocationsReadOnly": "All locations, read only",
    "defaultMark": "(default)",
    "never": "Never",
    "failedToday_one": "{{count}} failed try today",
    "failedToday_other": "{{count}} failed tries today"
  },
  "status": {
    "active": "Active",
    "deactivated": "Deactivated",
    "locked": "Locked"
  },
  "fields": {
    "fullName": "Full name",
    "username": "Username",
    "email": "Email",
    "role": "Role",
    "language": "Language",
    "locationAccess": "Where they can work",
    "locations": "Locations",
    "defaultLocation": "Default location",
    "password": "Password"
  },
  "roleHints": {
    "admin": "Everything, including users, configuration, company profile and backups.",
    "manager": "All stock work on all locations, validates corrections and counts, sees costs and reports.",
    "storekeeper": "Daily operations on assigned locations only. No cost prices.",
    "cashier": "Sells at the counter of their store: opens and closes their shift, takes payments. No cost prices.",
    "viewer": "Read-only access to stock, dashboards and reports."
  },
  "access": {
    "all": {
      "title": "All locations",
      "text": "Can work in every site, now and future ones."
    },
    "assigned": {
      "title": "Only assigned locations",
      "text": "Sees and operates only the locations picked below."
    },
    "noLocations": "No location exists yet. Create your sites in Locations first, then assign them here.",
    "none": "None",
    "full": "Full",
    "read": "Read",
    "readAll": "Read all",
    "own": "own",
    "ownActivity": "Own & own locations",
    "stockMovements": "Stock movements",
    "setUpAndReceive": "Set up & receive",
    "receive": "Receive",
    "quantitiesOnly": "Quantities only",
    "modules": {
      "company": "Company profile",
      "configuration": "Configuration",
      "users": "Users & roles",
      "products": "Products",
      "locations": "Locations",
      "stock": "Stock in & out",
      "transfers": "Transfers",
      "counts": "Inventory counts",
      "customers": "Customers",
      "orders": "Orders",
      "payments": "Payments",
      "registers": "Cash registers",
      "shifts": "Shifts",
      "pos": "Point of sale",
      "activity": "Activity log",
      "alerts": "Alerts",
      "analytics": "Dashboard & reports",
      "dataTools": "Data tools",
      "special": "Special permissions",
      "suppliers": "Suppliers",
      "supplierPayments": "Supplier payments",
      "promotions": "Promotions"
    },
    "actions": {
      "default": {
        "view": "Read",
        "create": "Create",
        "edit": "Edit",
        "validate": "Validate",
        "viewMovements": "Stock movements",
        "import": "Import",
        "export": "Export",
        "backup": "Backup & restore"
      },
      "transfers": {
        "create": "Send",
        "validate": "Receive"
      },
      "counts": {
        "create": "Count"
      },
      "orders": {
        "fulfill": "Prepare & hand over",
        "cancel": "Cancel"
      },
      "payments": {
        "record": "Take",
        "refund": "Refund"
      },
      "registers": {
        "manage": "Set up"
      },
      "shifts": {
        "open": "Open",
        "close": "Close",
        "viewAll": "Every shift"
      },
      "pos": {
        "sell": "Sell",
        "sellWholesale": "Sell wholesale"
      },
      "activity": {
        "view": "Read all"
      },
      "alerts": {
        "view": "Receive",
        "edit": "Set up"
      },
      "promotions": {
        "manage": "Manage"
      },
      "supplierPayments": {
        "record": "Record",
        "cancel": "Cancel"
      }
    }
  },
  "form": {
    "createTitle": "New user",
    "createSubtitle": "They will choose their own password at the first sign-in.",
    "editTitle": "Edit {{name}}",
    "create": "Create user",
    "saved": "{{name}} saved",
    "created": "{{name}} created",
    "deactivate": "Deactivate",
    "reactivate": "Reactivate",
    "deactivated": "{{name}} deactivated and signed out",
    "reactivated": "{{name}} reactivated",
    "usernameFixed": "The username never changes: it identifies the person in the history.",
    "usernameHint": "Lowercase letters, digits, dots and dashes.",
    "emailHint": "Used for “forgot password” emails.",
    "defaultLocationHint": "Pre-filled in their operations.",
    "noDefault": "No default",
    "passwordGenerate": {
      "title": "Generate a temporary password",
      "text": "Shown once, to hand over."
    },
    "passwordSet": {
      "title": "Type a password",
      "text": "They still change it at the first sign-in."
    },
    "confirmDeactivateTitle": "Deactivate {{name}}?",
    "confirmDeactivateText": "They are signed out at once and can no longer sign in. Their history stays, and you can reactivate them later.",
    "signIns": "Sign-in history"
  },
  "temporary": {
    "title": "Temporary password for {{name}}",
    "text": "Give it to them in person. It is shown only now; they choose their own password at the first sign-in.",
    "copy": "Copy",
    "copied": "Copied"
  },
  "reset": {
    "title": "Reset the password of {{name}}?",
    "text": "A temporary password is generated and every open session of this person is closed. They choose a new password at the next sign-in. A failed sign-in lockout is cleared too.",
    "confirm": "Reset password"
  },
  "matrix": {
    "title": "What each role can do",
    "subtitle": "Storekeepers and cashiers only see the locations they are assigned to",
    "edit": "Edit permissions",
    "area": "Area",
    "editTitle": "Edit permissions",
    "editSubtitle": "Changes apply at once to every person with the role, and are logged.",
    "adminLocked": "Administrators always have every permission, so nobody can be locked out of user management.",
    "saved": "Permissions saved"
  },
  "special": {
    "title": "Special permissions",
    "permission": "Permission",
    "yes": "Yes",
    "no": "No",
    "items": {
      "confirmNegativeStock": {
        "title": "Confirm negative stock",
        "text": "Validate even if a quantity goes below zero"
      },
      "quickTransfer": {
        "title": "Quick transfer",
        "text": "Send and receive between sites in one step"
      },
      "approveLargeAdjustments": {
        "title": "Approve large corrections",
        "text": "Validate adjustments above the limit"
      },
      "seeCosts": {
        "title": "See costs and values",
        "text": "Cost prices, average cost and stock value in TND"
      },
      "giveLargeDiscount": {
        "title": "Give large discounts",
        "text": "Discounts above the limit set for the counter"
      },
      "refundWithoutReceipt": {
        "title": "Refund without a receipt",
        "text": "Return goods when the sale cannot be found"
      },
      "voidSale": {
        "title": "Void a sale",
        "text": "Cancel a paid sale of an open shift"
      },
      "approveCashVariance": {
        "title": "Approve cash differences",
        "text": "Close a shift whose counted cash differs above the limit"
      },
      "openDrawer": {
        "title": "Open the cash drawer",
        "text": "Open the drawer without a sale"
      },
      "acceptLateReturn": {
        "title": "Accept late returns",
        "text": "Take back items after the return period"
      }
    }
  }
}
